import React, { useState, useEffect, useMemo, useRef } from "react";
import { DataGridPro, ToolbarButton } from '@mui/x-data-grid-pro';
import DefaultToolbar from "src/components/DataGrid/DefaultToolbar";
import { Form } from '@formio/react';
import { Box, Dialog, DialogTitle, DialogActions, DialogContent, Button, Typography, CircularProgress, Tooltip, Autocomplete, TextField, Snackbar, Alert, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { generateFormsPdf, loadCompanyLogo, isAbortError } from './formsPdf';
import FormQualityCheck from 'src/components/forms/GuidanceWindow/FormQualityCheck';
import FormAttachments, { uploadTaskFiles } from 'src/components/forms/FormAttachments/FormAttachments';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import axios from "axios";
import { APIURL } from "../../../config";
import moment from "moment";
import { convertTime } from "../../../utils/time";
import { schemaForCustomerForm } from './formSchema';
import './Forms.css'

const TIMESTAMP_FORMAT = 'MM/DD/YYYY hh:mm A';

const formatTaskWindow = (formObj) => {
    const toLocal = (value) => (
        value
            ? convertTime(value, formObj?.empLocate_timezone, moment.tz.guess())
                ?? moment(value).utc().format(TIMESTAMP_FORMAT)
            : ''
    );

    return { checkIn: toLocal(formObj?.empLocdate), checkOut: toLocal(formObj?.checkoutEmpDate) };
};

const toGridDate = (value, fromTimezone) => {
    if (!value) return null;

    const local = fromTimezone
        ? convertTime(value, fromTimezone, moment.tz.guess(), 'YYYY-MM-DDTHH:mm:ss')
        : null;
    const parsed = local ? moment(local, 'YYYY-MM-DDTHH:mm:ss') : moment(value);

    return parsed.isValid() ? parsed.toDate() : null;
};

const uniqueGoals = (cusGoals) => (
    Array.isArray(cusGoals)
        ? Array.from(new Map(
            cusGoals
                .filter(goal => goal && goal.goal_title)
                .map(goal => [goal.customer_goal_id, goal])
        ).values())
        : []
);

/** Shapes one customer form row into the document `generateFormsPdf` expects. */
const buildFormDocument = (formObj, { logo, companyName, customerName }) => {
    const { checkIn, checkOut } = formatTaskWindow(formObj);
    const goals = uniqueGoals(formObj.cusGoals).map(goal => goal.goal_title);

    return {
        label: formObj.form_name,
        form: schemaForCustomerForm(formObj),
        submission: formObj.formData,
        header: {
            logo,
            companyName: companyName || '',
            line2: `${formObj.form_name} for ${customerName || ''} by ${formObj.emp_first_name || ''} ${formObj.emp_last_name || ''}`,
            line3: `${formObj.tas_title || ''} (${checkIn} - ${checkOut})`,
            goals
        }
    };
};

/**
 * Declared out here on purpose: an arrow function written inline in `slots` is a new
 * component *type* on every render, so React tears the toolbar down and rebuilds it
 * each time — which during an export meant the progress spinner restarting once per
 * form and the form picker losing whatever had been typed into it.
 */
const FormsToolbar = ({
    subscribedForms,
    selectedMenuForm,
    onSelectMenuForm,
    onAddForm,
    onGeneratePdf,
    onCancelPdf,
    hasSelection,
    isGenerating,
    generationProgress,
}) => {
    return (
        <div>
            <div style={{ display: 'flex', padding: 5, marginBottom: -50 }}>
                <Autocomplete
                    id="form-select"
                    options={subscribedForms}
                    value={selectedMenuForm}
                    getOptionLabel={(option) => option.form_name}
                    isOptionEqualToValue={(option, value) => option.form_id === value.form_id}
                    onChange={(event, value) => { onSelectMenuForm(value); }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.form_id}>
                            {option.form_name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Form" size='small' />}
                    sx={{ width: 250, marginRight: '10px' }}
                />
                <Button
                    variant="contained"
                    onClick={onAddForm}
                    sx={{ height: '40px' }}
                    disabled={!selectedMenuForm}
                >
                    Add Form
                </Button>
            </div>
            <DefaultToolbar>
                {/* A disabled button fires no events, so the tooltip needs a live wrapper. */}
                <Tooltip title="Bulk Download">
                    <span>
                        <ToolbarButton onClick={() => onGeneratePdf(true)} disabled={!hasSelection || isGenerating}>
                            <FileDownloadIcon />
                        </ToolbarButton>
                    </span>
                </Tooltip>
                <Tooltip title="Bulk Print">
                    <span>
                        <ToolbarButton onClick={() => onGeneratePdf(false)} disabled={!hasSelection || isGenerating}>
                            <PrintIcon />
                        </ToolbarButton>
                    </span>
                </Tooltip>
                {isGenerating &&
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size="20px" />
                        {generationProgress &&
                            <Typography variant="caption" color="text.secondary">
                                {generationProgress.done}/{generationProgress.total}
                            </Typography>
                        }
                        <Button size="small" color="inherit" onClick={onCancelPdf}>
                            Cancel
                        </Button>
                    </Box>
                }
            </DefaultToolbar>
        </div>
    );
};

export default function Forms(props) {
    const [formsData, setFormsData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(null);
    const [pendingPrintUrl, setPendingPrintUrl] = useState(null);
    const [selectionModel, setSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    const [customerProjects, setCustomerProjects] = useState([]);
    const [projectLocations, setProjectLocations] = useState([]);
    const [subscribedForms, setSubscribedForms] = useState([]);
    const [selectedMenuForm, setSelectedMenuForm] = useState(null);

    // Live submissions for the two form dialogs. <Form> is uncontrolled here, so
    // between opening and submitting, this is the only readable copy of what the
    // user has typed -- which is what the quality check needs to evaluate.
    const editDraftRef = useRef(null);
    const addDraftRef = useRef(null);

    // Drop the captured draft whenever the dialog switches to a different form,
    // otherwise opening form B without typing would send form A's answers.
    useEffect(() => { editDraftRef.current = null; }, [selectedForm?.form_id]);
    useEffect(() => { addDraftRef.current = null; }, [selectedMenuForm?.form_id]);
    const [addFormDialogOpen, setAddFormDialogOpen] = useState(false);
    const [addingForm, setAddingForm] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [formStartTime, setFormStartTime] = useState(null);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [claimStatus, setClaimStatus] = useState(null);
    const [claimStatusLoading, setClaimStatusLoading] = useState(false);

    const isGeneratingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const objectUrlsRef = useRef(new Set());

    useEffect(() => {
        fetchData();
    }, []);

    // Blob URLs outlive the component unless they are handed back, and an export left
    // running past unmount is work nobody is waiting for any more.
    useEffect(() => {
        const objectUrls = objectUrlsRef.current;
        return () => {
            objectUrls.forEach((url) => URL.revokeObjectURL(url));
            objectUrls.clear();
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (open && selectedForm?.tas_claim_id) {
            setClaimStatusLoading(true);
            axios.get(APIURL + `/claims?claim_id=${selectedForm.tas_claim_id}`)
                .then(res => setClaimStatus(res.data.Status))
                .catch(() => setClaimStatus(null))
                .finally(() => setClaimStatusLoading(false));
        } else {
            setClaimStatus(null);
        }
    }, [open, selectedForm]);

    const fetchData = async () => {
        setLoadError('');
        await axios
            .get(APIURL + `/project?cus_id=${props.cus_id}`)
            .then(async function (response) {
                let customerProjects = response.data.filter(project => project.pro_is_active);
                setCustomerProjects(customerProjects);

                if (customerProjects.length) {
                    await axios
                        .get(APIURL + `/projlocations?project_id=${customerProjects[0]?.pro_id}`)
                        .then(function (response) {
                            setProjectLocations(response.data.filter(location => location.loc_is_active))
                        })
                        .catch(function (error) {
                            console.log(error);
                        });
                };
            })
            .catch(function (error) {
                console.log(error);
            });

        try {
            const subscribedForms = await axios.get(`${APIURL}/task_form/subscribed?company_id=${props.comp_id}`);
            if (subscribedForms.data.length > 0) {
                setSubscribedForms(subscribedForms.data);
            }

            const response = await axios.get(APIURL + '/customer_form/customer/' + props.cus_id);
            if (response.data.length > 0) {
                setFormsData(response.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoadError(error.response?.data?.message || 'Unable to load completed forms. Please try again.');
        }

        setLoading(false);
    };

    const handleSubmit = async (submission) => {
        try {
            const response = await axios.put(APIURL + '/customer_form/' + selectedForm.form_id, {
                data: JSON.stringify(submission),
                emp_id: props.user.emp_id
            });

            setOpen(false);
            setSelectedForm(null);
        } catch (error) {
            console.error('Error submitting data:', error);
        }
    }

    const columns = useMemo(() => [
        { field: "form_id", headerName: "Form ID", width: 100, valueFormatter: (value) => value ?? '—' },
        { field: "form_name", headerName: "Form Name", width: 150 },
        {
            field: "view_edit_form",
            headerName: "View/Edit",
            width: 90,
            renderCell: (params) => {
                const onClickView = async () => {
                    setSelectedForm(params.value);
                    setOpen(true);
                    setIsEdit(false);
                };

                const onClickEdit = async () => {
                    setSelectedForm(params.value);
                    setOpen(true);
                    setIsEdit(true);
                };
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={onClickView}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {!params.value?.is_referral && <>
                            <div style={{ marginLeft: 3, marginRight: 3 }}> | </div>
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={onClickEdit}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </>}
                    </div>
                )
            }
        },
        { field: "task_title", headerName: "Task Title", width: 150 },
        {
            field: "task_date",
            headerName: "Task Date",
            width: 100,
            type: 'date',
            valueFormatter: (value) => (value ? moment(value).format('MM/DD/YYYY') : ''),
        },
        {
            field: "last_updated",
            headerName: "Form Last Updated",
            width: 175,
            type: 'dateTime',
            valueFormatter: (value) => (value ? moment(value).format(TIMESTAMP_FORMAT) : ''),
        },
        { field: "completed_by", headerName: "Completed By", width: 200 },
    ], []);

    const rows = useMemo(() => {
        const seen = new Map();

        return formsData.map((form) => {
            const identity = form.is_referral
                ? `referral-${form.referral_id}`
                : [form.form_id, form.empLocdate ?? '', form.checkoutEmpDate ?? ''].join('|');
            const occurrence = seen.get(identity) ?? 0;
            seen.set(identity, occurrence + 1);

            return {
                id: occurrence ? `${identity}#${occurrence}` : identity,
                form_id: form.form_id,
                form_name: form.form_name,
                task_title: form.tas_title,
                task_date: toGridDate(form.checkoutEmpDate || form.referral_date, form.empLocate_timezone),
                last_updated: toGridDate(form.last_updated),
                completed_by: (form.emp_first_name ? form.emp_first_name + ' ' + form.emp_last_name : '-'),
                view_edit_form: form,
            };
        });
    }, [formsData]);

    /** The forms currently ticked in the grid, in the order the grid holds them. */
    const selectedForms = () => rows
        .filter(row => selectionModel.ids.has(row.id))
        .map(row => row.view_edit_form)
        .filter(Boolean);

    /**
     * Renders the given forms into one PDF. Takes its forms as an argument rather than
     * reading the grid selection, so the dialog can print the form it is showing
     * without having to reach in and overwrite what the user has ticked.
     */
    const generatePdf = async (forms, downloadPdf = true) => {
        const targets = (forms || []).filter(Boolean);
        if (!targets.length || isGeneratingRef.current) return;

        isGeneratingRef.current = true;
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsGenerating(true);
        setGenerationProgress({ done: 0, total: targets.length });

        try {
            const logo = await loadCompanyLogo(props.user?.company_logo);

            const documents = targets.map(formObj => buildFormDocument(formObj, {
                logo,
                companyName: props.user?.company_name,
                customerName: props.cust_details?.cus_name,
            }));

            const { pdf, skipped } = await generateFormsPdf({
                documents,
                signal: controller.signal,
                onProgress: (done, total) => setGenerationProgress({ done, total })
            });

            if (downloadPdf) {
                pdf.save(`forms_${moment().format('YYYYMMDD_HHmmss')}.pdf`);
            } else {
                openPdfInNewTab(pdf);
            }

            // A form that renders blank or runs past the page cap still produces a PDF;
            // saying which ones is the difference between a short export and a wrong one.
            if (skipped.length) {
                const named = skipped.map(item => `"${item.label}" ${item.reason}`).join('; ');
                setAlertMessage(`${skipped.length} of ${documents.length} form(s) are incomplete in this PDF — ${named}.`);
                setAlertType('warning');
            }
        } catch (error) {
            if (isAbortError(error)) return;
            console.error('Error generating bulk PDF:', error);
            setAlertMessage(error?.userMessage || 'An error occurred while generating the PDF!');
            setAlertType('error');
        } finally {
            isGeneratingRef.current = false;
            abortControllerRef.current = null;
            setIsGenerating(false);
            setGenerationProgress(null);
        }
    };

    const cancelPdf = () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
    };

    const trackObjectUrl = (blob) => {
        const url = URL.createObjectURL(blob);
        objectUrlsRef.current.add(url);
        return url;
    };

    const releaseObjectUrl = (url) => {
        if (!url) return;
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
    };

    const openPdfInNewTab = (pdf) => {
        const url = trackObjectUrl(pdf.output('blob'));
        const tab = window.open(url, '_blank');

        if (tab) {
            // Give the new tab time to load the blob before reclaiming it.
            setTimeout(() => releaseObjectUrl(url), 60000);
        } else {
            setPendingPrintUrl(url);
        }
    };

    const handleOpenPendingPrint = () => {
        if (!pendingPrintUrl) return;
        const url = pendingPrintUrl;
        window.open(url, '_blank');
        setTimeout(() => releaseObjectUrl(url), 60000);
        setPendingPrintUrl(null);
    };

    const handleDismissPendingPrint = () => {
        releaseObjectUrl(pendingPrintUrl);
        setPendingPrintUrl(null);
    };

    const pendingPrintSnackbar = () => {
        return (
            <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    severity="info"
                    elevation={6}
                    variant="filled"
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button color="inherit" size="small" onClick={handleOpenPendingPrint}>
                                Open PDF
                            </Button>
                            <IconButton sx={{ p: 0.25 }} onClick={handleDismissPendingPrint}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    }
                >
                    Your PDF is ready.
                </Alert>
            </Snackbar>
        );
    };

    const formDialog = () => {
        const dialogGoals = uniqueGoals(selectedForm?.cusGoals);
        const billingLocked = claimStatus && !['Ready to Bill', 'Rejected', 'Denied'].includes(claimStatus);

        return (
            <Dialog
                open={open}
                onClose={() => {
                    if (!isEdit) {
                        setOpen(false);
                        setSelectedForm(null);
                    }
                }}
                maxWidth='md'
                fullWidth
                scroll='body'
                disableRestoreFocus
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 24px',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedForm?.form_name} <span style={{ fontWeight: 'normal' }}>for</span> {props.cust_details?.cus_name}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{
                                textAlign: 'right',
                                marginRight: '16px',
                                paddingRight: '16px',
                                borderRight: '1px solid #e0e0e0'
                            }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                    {selectedForm?.tas_title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatTaskWindow(selectedForm).checkIn} - {formatTaskWindow(selectedForm).checkOut}
                                </Typography>
                            </Box>

                            <Tooltip
                                title={
                                    dialogGoals.length > 0 ? (
                                        <Box sx={{ maxWidth: 300 }}>
                                            {dialogGoals.map((cg, index) => (
                                                <Box key={cg.customer_goal_id} sx={{ mb: index < dialogGoals.length - 1 ? 2 : 0 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        {index + 1}. {cg.goal_title}:
                                                    </Typography>
                                                    <Typography variant="body2">{cg.goal_detail}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="subtitle2">No goals for this customer yet.</Typography>
                                    )
                                }
                                placement="right-start"
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                        minWidth: 'unset',
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '21px',
                                        padding: 0,
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            backgroundColor: '#0069d9',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                        },
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        fontWeight: 'medium',
                                        fontSize: '14px'
                                    }}>
                                        CG
                                    </Box>
                                </Button>
                            </Tooltip>
                        </Box>
                        {selectedForm?.tas_claim_id && (
                            claimStatusLoading
                                ? <CircularProgress size={18} />
                                : claimStatus && (
                                    <Chip
                                        label={`Billing Status: ${claimStatus}`}
                                        size="small"
                                        color={'success'}
                                    />
                                )
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {claimStatusLoading ? <CircularProgress size={24} sx={{ display: 'block', margin: '16px auto' }} /> : (() => {
                        const billingLocked = claimStatus && !['Ready to Bill', 'Rejected', 'Denied'].includes(claimStatus);
                        return (
                            <>
                                <Form
                                    form={schemaForCustomerForm(selectedForm)}
                                    submission={selectedForm?.formData}
                                    onChange={(submission) => {
                                        if (submission && submission.data) editDraftRef.current = submission;
                                    }}
                                    onSubmit={handleSubmit}
                                    options={{
                                        readOnly: isEdit && !billingLocked ? false : true,
                                        hide: {
                                            submit: isEdit && !billingLocked ? false : true,
                                        }
                                    }}
                                />
                                {isEdit && !billingLocked && !!selectedForm?.ai_quality_check && (
                                    <FormQualityCheck
                                        formType={selectedForm?.form_type}
                                        customerId={props.cus_id}
                                        taskId={selectedForm?.task_id}
                                        empId={props.user?.emp_id}
                                        getSubmission={() => editDraftRef.current || selectedForm?.formData}
                                    />
                                )}
                            </>
                        );
                    })()}
                </DialogContent>
                {(!isEdit || !claimStatusLoading) &&
                    <DialogActions sx={{ border: '1px solid #ccc', justifyContent: 'space-between' }}>
                        <FormAttachments
                            task_id={selectedForm?.task_id}
                            customer_id={props.cus_id}
                            user_id={props.user?.emp_id}
                            readOnly={!isEdit || billingLocked}
                        />
                        {isEdit && !billingLocked &&
                        <div style={{ marginLeft: 'auto' }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => { setOpen(false); setSelectedForm(null); }}
                            >
                                Close
                            </Button>
                        </div>
                        }
                        {!isEdit &&
                        <div style={{ marginLeft: 'auto' }}>
                            <Button
                                variant="contained"
                                onClick={() => generatePdf([selectedForm], false)}
                                disabled={isGenerating}
                            >
                                Print
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => generatePdf([selectedForm], true)}
                                disabled={isGenerating}
                                sx={{ marginLeft: 1 }}
                            >
                                Download
                            </Button>
                        </div>
                        }
                    </DialogActions>
                }
            </Dialog>
        );
    };

    const addFormDialog = () => {
        return (
            <Dialog
                open={addFormDialogOpen}
                maxWidth='md'
                fullWidth
                scroll='body'
                disableRestoreFocus
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 24px',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedMenuForm?.form_name} <span style={{ fontWeight: 'normal' }}>for</span> {props.cust_details?.cus_name}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Form
                        form={JSON.parse(selectedMenuForm?.translated_json_schema ? selectedMenuForm?.translated_json_schema : selectedMenuForm?.json_schema)}
                        submission={selectedMenuForm?.formData}
                        onChange={(submission) => {
                            if (submission && submission.data) addDraftRef.current = submission;
                        }}
                        onSubmit={handleAddFormSubmit}
                        options={{
                            readOnly: false,
                            hide: {
                                submit: false,
                            }
                        }}
                    />
                    {!!selectedMenuForm?.ai_quality_check && (
                    <FormQualityCheck
                        formType={selectedMenuForm?.form_id}
                        customerId={props.cus_id}
                        empId={props.user?.emp_id}
                        getSubmission={() => addDraftRef.current || selectedMenuForm?.formData}
                    />
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-start', padding: '8px 24px 16px' }}>
                    <FormAttachments
                        pendingFiles={pendingAttachments}
                        onPendingChange={setPendingAttachments}
                    />
                    <div style={{ marginLeft: 'auto' }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                setAddFormDialogOpen(false);
                                setSelectedMenuForm(null);
                                setPendingAttachments([]);
                            }}
                        >
                            Close
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        );
    };

    const handleAddFormSubmit = async (submission) => {
        if (addingForm) {
            return;
        }

        setAddingForm(true);

        let taskID = null;

        // New Task Entry
        const taskData = {
            empId: props.user.emp_id,
            empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
            company_id: props.comp_id,
            tTitle: `New form filled for ${props.cust_details.cus_name} on ${moment().format('MM/DD/YYYY')}`,
            tDesc: 'Manual Customer Form Task',
            tProject: customerProjects.length ? customerProjects[0].pro_id : null,
            tPreReq: null,
            tType: 'Manual Customer Form',
            tStrtDate: moment(formStartTime).format('YYYY-MM-DDTHH:mm:ss'),
            tEndDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
            tStartTime: convertTime(formStartTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment(formStartTime).format('YYYY-MM-DDTHH:mm:ss'),
            tEndTime: convertTime(moment(), moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment().format('YYYY-MM-DDTHH:mm:ss'),
            tLoc: projectLocations.length ? projectLocations[0].loc_id : null,
            tLoc1: '',
            tLoc2: '',
            tAssTo: props.user.emp_id,
            tPriority: 'Low',
            tIsBillable: false,
            tas_required_sig: 0,
            tNotes: '',
            task_emp_timezone: props.user.emp_timezone || ''
        };

        await axios
            .post(APIURL + `/task`, taskData)
            .then(function (response) {
                taskID = response.data.id;
            })
            .catch(function (error) {
                console.log(error);
            });

        if (taskID) {
            // New EmpLocate Entry
            const empLocData = {
                projId: customerProjects.length ? customerProjects[0].pro_id : null,
                companyId: props.comp_id,
                empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
                taskTitle: `New form filled for ${props.cust_details.cus_name} on ${moment().format('MM/DD/YYYY')}`,
                empId: props.user.emp_id,
                taskId: taskID,
                locId: projectLocations.length ? projectLocations[0].loc_id : null,
                emploc_lat: projectLocations.length ? projectLocations[0].loc_lat : null,
                emploc_lon: projectLocations.length ? projectLocations[0].loc_lon : null,
                checkoutLocId: projectLocations.length ? projectLocations[0].loc_id : null,
                empLocate_timezone: moment.tz.guess()
            };

            await axios
                .post(APIURL + '/locate/empLoc', { ...empLocData, date: moment(formStartTime).format('YYYY-MM-DDTHH:mm:ss') })
                .then(function (response) {
                    console.log(response.data);
                })
                .catch(function (error) {
                    console.log(error);
                });

            await axios
                .put(APIURL + '/locate/empLoc', { ...empLocData, date: moment().format('YYYY-MM-DDTHH:mm:ss') })
                .then(function (response) {
                    console.log(response.data);
                })
                .catch(function (error) {
                    console.log(error);
                });

            // New Customer Form Entry
            const formData = {
                customer_id: props.cus_id,
                task_id: taskID,
                data: JSON.stringify(submission),
                form_type: selectedMenuForm.form_id,
                emp_id: props.user.emp_id
            }

            let formSaved = false;
            await axios
                .post(APIURL + `/customer_form`, formData)
                .then(function (response) {
                    formSaved = true;
                })
                .catch(function (error) {
                    console.log(error);
                    setAlertMessage('An error occurred while adding the form!');
                    setAlertType('error');
                });

            if (formSaved) {
                try {
                    await uploadTaskFiles(pendingAttachments, { task_id: taskID, customer_id: props.cus_id, user_id: props.user.emp_id });
                    setAlertMessage('Form added successfully!');
                    setAlertType('success');
                } catch (error) {
                    console.log(error);
                    setAlertMessage('Form added, but some attachments failed to upload!');
                    setAlertType('warning');
                }
            }
        } else {
            setAlertMessage('An error occurred while adding the form!');
            setAlertType('error');
        }

        setAddingForm(false);
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar
                    open={alertMessage ? true : false}
                    autoHideDuration={alertType === 'success' ? 2000 : 12000}
                    onClose={handleSnackbarAlertClose}
                >
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
        setAddFormDialogOpen(false);
        setSelectedMenuForm(null);
        setPendingAttachments([]);
        setFormStartTime(null);
        fetchData();
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className='forms_info' style={{ flex: 1, minHeight: 0, height: 'auto' }}>
                <div style={{ marginBottom: '10px' }}>
                    <h5>Forms</h5>
                </div>
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        initialState={{ sorting: { sortModel: [{ field: "last_updated", sort: "desc" }] } }}
                        checkboxSelection
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(newSelection) => {
                            if (newSelection.type === 'exclude') {
                                setSelectionModel({
                                    type: 'include',
                                    ids: new Set(
                                        rows.filter(r => !newSelection.ids.has(r.id)).map(r => r.id)
                                    ),
                                })
                            } else {
                                setSelectionModel(newSelection);
                            }
                        }}
                        disableRowSelectionOnClick
                        slots={{ toolbar: FormsToolbar }}
                        slotProps={{
                            toolbar: {
                                subscribedForms,
                                selectedMenuForm,
                                onSelectMenuForm: setSelectedMenuForm,
                                onAddForm: () => {
                                    setAddFormDialogOpen(true);
                                    setPendingAttachments([]);
                                    setFormStartTime(moment().format('YYYY-MM-DDTHH:mm:ss'));
                                },
                                onGeneratePdf: (downloadPdf) => generatePdf(selectedForms(), downloadPdf),
                                onCancelPdf: cancelPdf,
                                hasSelection: selectionModel.ids.size > 0,
                                isGenerating,
                                generationProgress,
                            },
                        }}
                        showToolbar
                        loading={isLoading}
                    />
                </Box>
                {open && formDialog()}
                {addFormDialogOpen && addFormDialog()}
                {alertMessage && SnackbarComponent()}
                {pendingPrintUrl && pendingPrintSnackbar()}
            </div>
        </div>
    );
}